// src/tabs/logements/LivretSectionsAutoEditor.jsx — Sprint A.9.4 PR-A93-2 (ADR-151)
//
// Éditeur admin sections auto IA livret voyageur.
// 3 cards (Assistance / Alentours / Marchés) avec toggle + génération IA + édition.
//
// Mitigations pessimist DEEP intégrées :
//   - F-MOY-1 : alentours_cache lookup AVANT OSM fetch (côté backend)
//   - F-MOY-2 : photo proxy fallback placeholder navy/sky si 404
//   - A11y : 44x44 touch targets, role="region", aria-labelledby, aria-live polite
//
// Cohérent UX :
//   - Toggle on/off : disabled actions si pas de content
//   - Bouton "🤖 Générer avec IA" si vide
//   - Bouton "✏️ Modifier" → modale édition JSONB structuré
//   - Bouton "🔄 Régénérer" warning écrasement modifications manuelles
//   - last_generated_at via formatDistanceToNow
//
// HOTFIX Sprint A.9.4 PR-A93-2-hotfix-bearer-token : récupération token via
//   supabase.auth.getSession() (helper getAuthHeaders local) — cohérent pattern
//   LivretInfosEditor + LivretModulesEditor. La prop token n'était pas passée
//   par le parent LivretModulesEditor, d'où bandeau "❌ Missing Bearer token".

import React, { useEffect, useState, useCallback } from "react";
import { LifeBuoy, Map as MapIcon, Sparkles, RefreshCw, Pencil, Trash2, AlertTriangle } from "lucide-react";
import { supabase } from "../../supabase";
// PR-FIX-4c (ADR-161) — éditeur visuel grid + drag-drop dédié pour la
// section 'alentours' (remplace SectionAutoEditModal textarea JSON). La modal
// SectionAutoEditModal reste disponible pour la section 'assistance' (D7 acté
// hors scope) + collapse "Édition avancée JSON" interne au nouvel éditeur.
import AlentoursAdminEditor from "./AlentoursAdminEditor";
// PR-FIX-5c (ADR-163) — éditeur visuel admin Assistance (remplace
// SectionAutoEditModal textarea JSON pour section_type='assistance').
// Permet le CRUD libre custom_contacts (vétérinaire, dentiste, taxi, etc.)
// + toggle displayed_to_guest sur les 3 contacts IA Google Places.
import AssistanceAdminEditor from "./AssistanceAdminEditor";

async function getAuthHeaders() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  return token
    ? { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
    : { "Content-Type": "application/json" };
}

// Rhabillage DA (DESIGN.md) — éditeur ADMIN : charte DS interne (Fraunces /
// Libre Franklin + marine/ciel/corail), pas la charte voyageur. Le DS local
// (ex-Raleway/Playfair) est repointé sur les tokens DA.
const DS = {
  navy: "#3d5166",
  sky: "#87b8e6",
  skyDeep: "#0284c7",
  terracotta: "#D85A30", // → corail (accent DA)
  charge: "#E24B4A",     // → danger
  green: "#1D9E75",      // → success
  fontBody: "'Libre Franklin', sans-serif",
  fontTitle: "'Fraunces', serif",
};

// Sprint A.9.5b (ADR-153) — card 'marches' SUPPRIMÉE (fusion dans alentours).
// Icônes lucide (trait fin) au lieu d'emoji (DESIGN §5).
const SECTION_META = {
  assistance: {
    Icon: LifeBuoy,
    label: "Assistance & Urgences",
    description: "Numéros d'urgence + médecin/pharmacie/hôpital proches",
  },
  alentours: {
    Icon: MapIcon,
    label: "Aux Alentours",
    description: "7 secteurs : restaurants, supermarchés, boulangeries, marchés, culture, parcs, transports",
  },
};

function _formatRelativeDate(iso) {
  if (!iso) return "Jamais généré";
  const ms = Date.now() - new Date(iso).getTime();
  if (isNaN(ms)) return "Jamais généré";
  const min = Math.floor(ms / 60000);
  if (min < 1) return "À l'instant";
  if (min < 60) return `Il y a ${min} min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `Il y a ${hr}h`;
  const days = Math.floor(hr / 24);
  return `Il y a ${days}j`;
}

async function _apiCall(action, options = {}) {
  const url = `/api/beds24-mapping?action=${action}`;
  const authHeaders = await getAuthHeaders();
  const res = await fetch(url, {
    method: options.method || "GET",
    headers: authHeaders,
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
  const json = await res.json().catch(() => ({ error: "parse_fail" }));
  if (!res.ok) {
    throw new Error(json.message || json.error || `HTTP ${res.status}`);
  }
  return json;
}

export default function LivretSectionsAutoEditor({ logement_id }) {
  const [rows, setRows] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatingType, setGeneratingType] = useState(null);
  const [editingType, setEditingType] = useState(null);

  const load = useCallback(async () => {
    if (!logement_id) return;
    setLoading(true);
    setError(null);
    try {
      const json = await _apiCall(`livret_sections_auto_get&logement_id=${encodeURIComponent(logement_id)}`);
      const byType = {};
      for (const r of json.rows || []) {
        byType[r.section_type] = r;
      }
      setRows(byType);
    } catch (e) {
      setError(e.message || "Erreur chargement sections");
    } finally {
      setLoading(false);
    }
  }, [logement_id]);

  useEffect(() => { load(); }, [load]);

  async function handleToggle(section_type, enabled) {
    try {
      await _apiCall("livret_sections_auto_toggle", {
        method: "POST",
        body: { logement_id, section_type, enabled },
      });
      await load();
    } catch (e) {
      setError(`Toggle ${section_type} échec : ${e.message}`);
    }
  }

  async function handleGenerate(section_type) {
    if (generatingType) return;
    setGeneratingType(section_type);
    setError(null);
    try {
      await _apiCall("livret_sections_auto_generate", {
        method: "POST",
        body: { logement_id, type: section_type },
      });
      await load();
    } catch (e) {
      setError(`Génération ${section_type} échec : ${e.message}`);
    } finally {
      setGeneratingType(null);
    }
  }

  async function handleDelete(section_type) {
    if (!window.confirm(`Supprimer la section ${SECTION_META[section_type].label} ?`)) return;
    try {
      await _apiCall("livret_sections_auto_delete", {
        method: "POST",
        body: { logement_id, section_type },
      });
      await load();
    } catch (e) {
      setError(`Suppression ${section_type} échec : ${e.message}`);
    }
  }

  if (!logement_id) return null;

  return (
    <div style={{ padding: "12px 0" }} aria-busy={loading}>
      <div
        style={{
          marginBottom: 16,
          padding: 14,
          background: "#f8f9fc",
          borderRadius: 10,
          border: `1px solid ${DS.sky}`,
        }}
      >
        <h3 style={{
          margin: 0, marginBottom: 6,
          fontFamily: DS.fontTitle, color: DS.navy, fontSize: 18,
        }}>
          Sections automatiques IA
        </h3>
        <p style={{ margin: 0, fontFamily: DS.fontBody, fontSize: 13, color: "#555" }}>
          Sections enrichies via Claude Haiku + Google Places + OpenStreetMap.
          Activez les sections souhaitées et générez le contenu en 1 clic.
        </p>
      </div>

      {error && (
        <div
          role="alert"
          aria-live="assertive"
          style={{
            display: "flex", alignItems: "center", gap: 9,
            padding: 10, marginBottom: 12,
            background: "#FCEBEB", border: `0.5px solid ${DS.charge}`,
            borderRadius: 8, color: "#A32D2D", fontFamily: DS.fontBody, fontSize: 13,
          }}
        >
          <AlertTriangle size={15} strokeWidth={1.8} style={{ flex: "0 0 auto" }} /> {error}
        </div>
      )}

      <div style={{ display: "grid", gap: 12 }}>
        {Object.keys(SECTION_META).map((section_type) => {
          const meta = SECTION_META[section_type];
          const row = rows[section_type];
          const hasContent = row?.content && Object.keys(row.content).length > 0;
          const isGenerating = generatingType === section_type;
          const enabled = !!row?.enabled;

          return (
            <div
              key={section_type}
              role="region"
              aria-labelledby={`section-${section_type}-title`}
              style={{
                padding: 14,
                background: "#fff",
                border: `1px solid ${enabled ? DS.skyDeep : "#dde3ec"}`,
                borderRadius: 10,
                opacity: enabled ? 1 : 0.7,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <h4
                    id={`section-${section_type}-title`}
                    style={{
                      margin: 0, marginBottom: 4,
                      display: "flex", alignItems: "center", gap: 8,
                      fontFamily: DS.fontTitle, color: DS.navy, fontSize: 16,
                    }}
                  >
                    <span style={{
                      display: "inline-flex", width: 28, height: 28, borderRadius: 8,
                      background: "#e4f0fb", color: DS.navy, alignItems: "center", justifyContent: "center",
                    }}>
                      <meta.Icon size={16} strokeWidth={1.8} />
                    </span>
                    {meta.label}
                  </h4>
                  <p style={{ margin: 0, fontSize: 12, color: "#666", fontFamily: DS.fontBody }}>
                    {meta.description}
                  </p>
                  {row?.last_generated_at && (
                    <p style={{ margin: "4px 0 0", fontSize: 11, color: "#888", fontFamily: DS.fontBody }}>
                      {_formatRelativeDate(row.last_generated_at)}
                    </p>
                  )}
                </div>

                {/* Toggle 44x44 touch target a11y */}
                <label
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    cursor: "pointer",
                    padding: 8,
                    minWidth: 44, minHeight: 44,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={enabled}
                    disabled={!hasContent}
                    onChange={(e) => handleToggle(section_type, e.target.checked)}
                    aria-label={`Activer section ${meta.label}`}
                    style={{ width: 22, height: 22, cursor: "pointer" }}
                  />
                  <span style={{
                    marginLeft: 6, fontSize: 12, fontWeight: 600,
                    color: enabled ? DS.green : "#888", fontFamily: DS.fontBody,
                  }}>
                    {enabled ? "Activé" : "Désactivé"}
                  </span>
                </label>
              </div>

              <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                {!hasContent && (
                  <button
                    type="button"
                    onClick={() => handleGenerate(section_type)}
                    disabled={isGenerating || generatingType !== null}
                    aria-live="polite"
                    className="fbz-press"
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 7,
                      padding: "8px 14px",
                      background: "#fff", color: DS.navy,
                      border: `0.5px solid #cdd5df`, borderRadius: 9,
                      cursor: isGenerating ? "wait" : "pointer",
                      fontFamily: DS.fontBody, fontSize: 13, fontWeight: 600,
                      minHeight: 44,
                    }}
                  >
                    <Sparkles size={15} strokeWidth={1.8} />
                    {isGenerating ? "Génération en cours…" : "Générer avec IA"}
                  </button>
                )}
                {hasContent && (
                  <>
                    <button
                      type="button"
                      onClick={() => setEditingType(section_type)}
                      className="fbz-press"
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 7,
                        padding: "8px 14px",
                        background: "#fff", color: DS.navy,
                        border: `0.5px solid #cdd5df`, borderRadius: 9,
                        cursor: "pointer",
                        fontFamily: DS.fontBody, fontSize: 13, fontWeight: 600,
                        minHeight: 44,
                      }}
                    >
                      <Pencil size={14} strokeWidth={1.8} /> Modifier
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm("Régénérer écrasera vos modifications manuelles. Confirmer ?")) {
                          handleGenerate(section_type);
                        }
                      }}
                      disabled={isGenerating || generatingType !== null}
                      className="fbz-press"
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 7,
                        padding: "8px 14px",
                        background: "#fff", color: DS.navy,
                        border: `0.5px solid #cdd5df`, borderRadius: 9,
                        cursor: isGenerating ? "wait" : "pointer",
                        fontFamily: DS.fontBody, fontSize: 13, fontWeight: 600,
                        minHeight: 44,
                      }}
                    >
                      <RefreshCw size={14} strokeWidth={1.8} />
                      {isGenerating ? "Régénération…" : "Régénérer"}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(section_type)}
                      className="fbz-press"
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 7,
                        padding: "8px 14px",
                        background: "#fff", color: DS.charge,
                        border: `0.5px solid #e8e8ea`, borderRadius: 9,
                        cursor: "pointer",
                        fontFamily: DS.fontBody, fontSize: 12, fontWeight: 600,
                        minHeight: 44,
                      }}
                    >
                      <Trash2 size={14} strokeWidth={1.8} /> Supprimer
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* PR-FIX-4c (ADR-161) + PR-FIX-5c (ADR-163) — dispatch sur section_type :
          - alentours → AlentoursAdminEditor inline (grid visuel + drag-drop)
          - assistance → AssistanceAdminEditor inline (CRUD libre contacts)
          - autres → SectionAutoEditModal historique (textarea JSON). */}
      {editingType === "alentours" && (
        <AlentoursAdminModal
          row={rows[editingType]}
          logement_id={logement_id}
          onClose={() => setEditingType(null)}
          onReload={async () => {
            await load();
          }}
        />
      )}
      {editingType === "assistance" && (
        <AssistanceAdminModal
          row={rows[editingType]}
          logement_id={logement_id}
          onClose={() => setEditingType(null)}
          onReload={async () => {
            await load();
          }}
        />
      )}
      {/* LOT 4 (décision Dimitri 2026-08-28) — la modale JSON générique
          SectionAutoEditModal a été SUPPRIMÉE : inatteignable depuis que les
          deux seuls section_type (alentours, assistance) ont leur éditeur
          dédié (ADR-161/163). */}
    </div>
  );
}

// ─ PR-FIX-5c (ADR-163) — Wrapper modal AssistanceAdminEditor ───────────────
//
// Affiche AssistanceAdminEditor dans une overlay modale plein écran (pattern
// identique AlentoursAdminModal).
function AssistanceAdminModal({ row, logement_id, onClose, onReload }) {
  useEffect(() => {
    function onEsc(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="assistance-admin-modal-title"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="fbz-modal-sheet"
        style={{
          background: "#fff",
          borderRadius: 12,
          padding: 20,
          maxWidth: 880,
          width: "100%",
          maxHeight: "90vh",
          overflow: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <h3
            id="assistance-admin-modal-title"
            style={{
              margin: 0,
              display: "flex", alignItems: "center", gap: 8,
              fontFamily: DS.fontTitle,
              color: DS.navy,
              fontSize: 18,
            }}
          >
            <LifeBuoy size={17} strokeWidth={1.8} /> Éditer Assistance & Urgences
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            style={{
              background: "transparent",
              border: "none",
              fontSize: 22,
              cursor: "pointer",
              color: "#666",
              padding: "0 6px",
            }}
          >
            ×
          </button>
        </div>
        <AssistanceAdminEditor
          logement_id={logement_id}
          row={row}
          onReload={onReload}
        />
      </div>
    </div>
  );
}

// ─ PR-FIX-4c (ADR-161) — Wrapper modal AlentoursAdminEditor ────────────────
//
// Affiche AlentoursAdminEditor dans une overlay modale plein écran pour
// préserver le pattern UX existant (LivretSectionsAutoEditor déclenche
// l'édition via "✏️ Modifier" → modale).
//
// Compatible avec la nouvelle UI grid (Drag-drop + boutons + Add modal) tout
// en gardant un escape handler + close overlay.
function AlentoursAdminModal({ row, logement_id, onClose, onReload }) {
  useEffect(() => {
    function onEsc(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="alentours-admin-modal-title"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="fbz-modal-sheet"
        style={{
          background: "#fff",
          borderRadius: 12,
          padding: 20,
          maxWidth: 880,
          width: "100%",
          maxHeight: "90vh",
          overflow: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <h3
            id="alentours-admin-modal-title"
            style={{
              margin: 0,
              display: "flex", alignItems: "center", gap: 8,
              fontFamily: DS.fontTitle,
              color: DS.navy,
              fontSize: 18,
            }}
          >
            <MapIcon size={17} strokeWidth={1.8} /> Éditer Aux Alentours
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            style={{
              background: "transparent",
              border: "none",
              fontSize: 22,
              cursor: "pointer",
              color: "#666",
              padding: "0 6px",
            }}
          >
            ×
          </button>
        </div>
        <AlentoursAdminEditor
          logement_id={logement_id}
          row={row}
          onReload={onReload}
        />
      </div>
    </div>
  );
}

